<template>
  <IonModal :is-open="isOpen" @did-dismiss="$emit('close')">
    <IonHeader>
      <div class="date-picker-header">
        <IonTitle>{{ title || t('common.selectDate') }}</IonTitle>
        <IonButtons slot="end">
          <IonButton @click="$emit('close')">
            <IonIcon :icon="close" />
          </IonButton>
        </IonButtons>
      </div>
    </IonHeader>
    <div :class="['date-picker-content', { 'time-only': isTimeOnly }]">
      <VDatePicker
        v-model="selectedDate"
        :mode="calendarMode"
        :locale="currentLocale"
        :is24hr="true"
        :hide-time-header="false"
        :time-accuracy="2"
        expanded
        borderless
        class="date-picker"
      />
      <div class="date-picker-actions">
        <Button @click="$emit('close')">
          {{ t('common.cancel') }}
        </Button>
        <Button @click="confirmSelection">
          {{ t('common.confirm') }}
        </Button>
      </div>
    </div>
  </IonModal>
</template>

<script setup lang="ts">
import { ref, computed, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { IonModal, IonHeader, IonTitle, IonButtons, IonButton, IonIcon } from '@ionic/vue';
import { close } from 'ionicons/icons';
import { DatePicker as VDatePicker } from 'v-calendar';
import 'v-calendar/style.css';
import Button from '@/common/components/ui/button/Button.vue';

interface Props {
  isOpen: boolean;
  initialDate?: Date | string;
  title?: string;
  /**
   * Mode du picker:
   * - 'date': sélection de date uniquement
   * - 'dateTime': sélection de date et heure
   * - 'time': sélection d'heure uniquement
   */
  mode?: 'date' | 'dateTime' | 'time';
}

const props = withDefaults(defineProps<Props>(), {
  initialDate: () => new Date(),
  mode: 'date',
});

const emit = defineEmits<{
  close: [];
  confirm: [date: Date];
}>();

const { t, locale } = useI18n();

const normalizeDate = (date: Date | string): Date => {
  return typeof date === 'string' ? new Date(date) : new Date(date);
};

const selectedDate = ref<Date>(normalizeDate(props.initialDate));

const currentLocale = computed(() => {
  return locale.value.startsWith('fr') ? 'fr-FR' : 'en-US';
});

/**
 * v-calendar mode mapping:
 * - 'date' -> no time picker
 * - 'dateTime' -> with time picker
 * - 'time' -> time only (using dateTime mode with calendar hidden via CSS)
 */
const calendarMode = computed(() => {
  return props.mode === 'date' ? 'date' : 'dateTime';
});

/**
 * Check if we should hide the calendar (time-only mode)
 */
const isTimeOnly = computed(() => props.mode === 'time');

// Reset selected date when modal opens
watch(
  () => props.isOpen,
  (isOpen) => {
    if (isOpen) {
      selectedDate.value = normalizeDate(props.initialDate);
    }
  }
);

// Update when initialDate changes
watch(
  () => props.initialDate,
  (newDate) => {
    if (newDate) {
      selectedDate.value = normalizeDate(newDate);
    }
  }
);

const confirmSelection = () => {
  emit('confirm', selectedDate.value);
};
</script>

<style scoped>
ion-header {
  --background: var(--ion-background-color);
  --color: var(--ion-text-color);
  box-shadow: none;
  border-bottom: 1px solid var(--ion-background-color-tertiary);
}

.date-picker-content {
  padding: 16px;
  display: flex;
  flex-direction: column;
  align-items: center;
}

.date-picker-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 16px;
  height: 56px;
  background-color: var(--ion-background-color);
  color: var(--ion-text-color);
}

.date-picker-actions {
  display: flex;
  justify-content: center;
  gap: 16px;
  margin-top: 24px;
  width: 100%;
}

/* v-calendar customization - fully stylable without Shadow DOM */
.date-picker {
  width: 100%;
  max-width: 350px;
  font-family: inherit;
}

/* Override v-calendar CSS variables to match app theme */
:deep(.vc-container) {
  --vc-bg: var(--ion-background-color);
  --vc-border: var(--ion-background-color-tertiary);
  --vc-color: var(--ion-text-color);
  --vc-accent-50: var(--ion-color-primary-tint);
  --vc-accent-100: var(--ion-color-primary-tint);
  --vc-accent-200: var(--ion-color-primary-tint);
  --vc-accent-300: var(--ion-color-primary-tint);
  --vc-accent-400: var(--ion-color-primary);
  --vc-accent-500: var(--ion-color-primary);
  --vc-accent-600: var(--ion-color-primary);
  --vc-accent-700: var(--ion-color-primary-shade);
  --vc-accent-800: var(--ion-color-primary-shade);
  --vc-accent-900: var(--ion-color-primary-shade);
  --vc-gray-50: var(--ion-background-color);
  --vc-gray-100: var(--ion-background-color-secondary);
  --vc-gray-200: var(--ion-background-color-tertiary);
  --vc-gray-300: var(--ion-color-medium);
  --vc-gray-400: var(--ion-color-medium);
  --vc-gray-500: var(--ion-color-medium);
  --vc-gray-600: var(--ion-text-color);
  --vc-gray-700: var(--ion-text-color);
  --vc-gray-800: var(--ion-text-color);
  --vc-gray-900: var(--ion-text-color);
  --vc-font-family: inherit;
  background-color: var(--ion-background-color);
  border: none;
  border-radius: 12px;
}

/* Calendar header (month/year navigation) */
:deep(.vc-header) {
  padding: 0px 8px;
}

:deep(.vc-title) {
  background: transparent;
  color: var(--ion-text-color);
  font-weight: 600;
}

:deep(.vc-arrow) {
  color: var(--ion-text-color);
  background: transparent;
  border-radius: 8px;
}

:deep(.vc-arrow:hover) {
  background: var(--ion-background-color-tertiary);
}

:deep(.vc-nav-arrow) {
  background: transparent;
}

:deep(.vc-nav-title) {
  background: transparent;
}

:deep(.vc-nav-item) {
  background: transparent;
  color: var(--ion-text-color);
}

/* Weekday headers */
:deep(.vc-weekday) {
  color: var(--ion-color-medium);
  font-weight: 500;
}

/* Calendar days */
:deep(.vc-day) {
  min-height: 36px;
}

:deep(.vc-day-content) {
  color: var(--ion-text-color);
  font-weight: 400;
  border-radius: 50%;
  width: 32px;
  height: 32px;
}

:deep(.vc-day-content:hover) {
  background: var(--ion-background-color-tertiary);
}

:deep(.vc-day-content:focus) {
  background: var(--ion-background-color-tertiary);
}

/* Selected day */
:deep(.vc-highlight) {
  background: var(--ion-background-color-tertiary) !important;
  border-radius: 50%;
  border: 1px solid var(--ion-text-color) !important;
}

:deep(.vc-highlight-content-solid) {
  color: var(--ion-color-primary-contrast) !important;
}

/* Time picker section */
:deep(.vc-time-picker) {
  padding: 16px 12px;
  border-top: 1px solid var(--ion-background-color-tertiary);
  background: var(--ion-background-color-secondary);
  border-radius: 0 0 12px 12px;
}

:deep(.vc-time-header) {
  color: var(--ion-text-color);
  font-weight: 600;
  margin-bottom: 8px;
}

/* Time inputs */
:deep(.vc-time-select-group) {
  background: var(--ion-background-color);
  border: 1px solid var(--ion-background-color-tertiary);
  border-radius: 8px;
  padding: 8px 12px;
}

:deep(.vc-base-select) {
  background: transparent;
}

:deep(.vc-base-select select) {
  color: var(--ion-text-color);
  background: transparent;
  border: none;
  font-size: 16px;
  font-weight: 500;
  padding: 4px;
  cursor: pointer;
}

:deep(.vc-base-select select:focus) {
  outline: none;
}

/* Time separator */
:deep(.vc-time-colon) {
  color: var(--ion-text-color);
  font-weight: 600;
}

/* AM/PM toggle if in 12h mode */
:deep(.vc-am-pm) {
  background: var(--ion-background-color-tertiary);
  border-radius: 6px;
  padding: 4px 8px;
}

/* Date selection indicator */
:deep(.vc-day.is-today .vc-day-content) {
  border: 2px solid var(--ion-color-primary);
}

/* Disabled days */
:deep(.vc-day-content.is-disabled) {
  color: var(--ion-color-medium);
  opacity: 0.5;
}

/* Time-only mode: hide calendar */
.time-only :deep(.vc-pane-container) {
  display: none;
}

.time-only :deep(.vc-time-picker) {
  border-top: none;
  border-radius: 12px;
}
</style>
